import { forwardRef, useImperativeHandle, useRef, useEffect } from 'react'

export interface SignaturePadHandle {
  clear: () => void
  isEmpty: () => boolean
  toBlob: () => Promise<Blob | null>
}

// Lightweight canvas-based signature capture (no external dependency).
// Draws with pointer events so it works with mouse, touch, and stylus alike.
const SignaturePad = forwardRef<SignaturePadHandle, { height?: number }>(({ height = 200 }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing   = useRef(false)
  const hasDrawn  = useRef(false)
  const last      = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ratio = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width  = rect.width * ratio
    canvas.height = rect.height * ratio
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.scale(ratio, ratio)
      ctx.lineWidth   = 2.2
      ctx.lineCap     = 'round'
      ctx.lineJoin    = 'round'
      ctx.strokeStyle = '#1e293b'
    }
  }, [])

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true
    hasDrawn.current = true
    last.current = pointerPos(e)
    ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
  }
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    const pos = pointerPos(e)
    if (ctx && last.current) {
      ctx.beginPath()
      ctx.moveTo(last.current.x, last.current.y)
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
    }
    last.current = pos
  }
  const end = () => { drawing.current = false; last.current = null }

  useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      hasDrawn.current = false
    },
    isEmpty: () => !hasDrawn.current,
    toBlob: () => new Promise(resolve => {
      const canvas = canvasRef.current
      if (!canvas) return resolve(null)
      canvas.toBlob(blob => resolve(blob), 'image/png')
    }),
  }))

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height, touchAction: 'none' }}
      className="bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-crosshair"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
    />
  )
})

export default SignaturePad
